import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'sonner';
import { HelmetProvider } from 'react-helmet-async';
import './i18n';
import './App.css';
import { AuthProvider, useAuth } from './context/AuthContext';
import { CartProvider } from './context/CartContext';
import { CurrencyProvider } from './context/CurrencyContext';
import { OfflineProvider } from './context/OfflineContext';

// Components
import { Navbar } from './components/Navbar';
import { Footer } from './components/Footer';
import OfflineWrapper from './components/OfflineWrapper';

// Public Pages
import LandingPage from './pages/LandingPage';
import RestaurantOSLandingPage from './pages/RestaurantOSLandingPage';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import AuthCallbackPage from './pages/AuthCallbackPage';
import ProfilePage from './pages/ProfilePage';
import RestaurantsPage from './pages/RestaurantsPage';
import RestaurantDetailPage from './pages/RestaurantDetailPage';
import BookTablePage from './pages/BookTablePage';
import TableOrderingPage from './pages/TableOrderingPage';
import CartPage from './pages/CartPage';
import OrdersPage from './pages/OrdersPage';
import OrderDetailPage from './pages/OrderDetailPage';
import OrderSuccessPage from './pages/OrderSuccessPage';
import NotificationsPage from './pages/NotificationsPage';
import NotificationSettingsPage from './pages/NotificationSettingsPage';

// Restaurant Panel
import RestaurantLayout from './pages/restaurant/RestaurantLayout';
import RestaurantDashboard from './pages/restaurant/RestaurantDashboard';
import MenuManagement from './pages/restaurant/MenuManagement';
import RestaurantOrders from './pages/restaurant/RestaurantOrders';
import TableBookings from './pages/restaurant/TableBookings';
import TableManagement from './pages/restaurant/TableManagement';
import WaiterManagement from './pages/restaurant/WaiterManagement';
import StaffManagement from './pages/restaurant/StaffManagement';
import RestaurantSettings from './pages/restaurant/RestaurantSettings';
import DriverMap from './pages/restaurant/DriverMap';
import SupportTickets from './pages/restaurant/SupportTickets';
import RestaurantFinancialReports from './pages/restaurant/FinancialReports';
import POS from './pages/restaurant/POS';
import Kitchen from './pages/restaurant/Kitchen';
import StockManagement from './pages/restaurant/StockManagement';
import LoyaltyProgram from './pages/restaurant/LoyaltyProgram';
import EODReports from './pages/restaurant/EODReports';
import PrintSettingsPage from './pages/restaurant/PrintSettings';
import VeriFactuSettings from './pages/restaurant/VeriFactuSettings';
import VeriFactuDashboard from './pages/restaurant/VeriFactuDashboard';
import LiveTables from './pages/restaurant/LiveTables';

// Admin Panel
import AdminLayout from './pages/admin/AdminLayout';
import AdminDashboard from './pages/admin/AdminDashboard';
import ServerHealthDashboard from './pages/admin/ServerHealthDashboard';
import SubscriptionManagement from './pages/admin/SubscriptionManagement';
import UserManagement from './pages/admin/UserManagement';
import FinancialReports from './pages/admin/FinancialReports';
import AuditLogs from './pages/admin/AuditLogs';
import RestaurantManagement from './pages/admin/RestaurantManagement';
import PromoCodes from './pages/admin/PromoCodes';
import AnalyticsDashboard from './pages/admin/AnalyticsDashboard';
import ApplicationsManagement from './pages/admin/ApplicationsManagement';
import DriverDocumentVerification from './pages/admin/DriverDocumentVerification';
import DocumentArchives from './pages/admin/DocumentArchives';
import DeliverySettings from './pages/admin/DeliverySettings';
import DriverPerformance from './pages/admin/DriverPerformance';
import SalespersonPayouts from './pages/admin/SalespersonPayouts';
import SetupWizard from './pages/admin/SetupWizard';
import TicketManagement from './pages/admin/TicketManagement';
import PlatformSettings from './pages/admin/PlatformSettings';
import NotificationManagement from './pages/admin/NotificationManagement';
import BroadcastNotifications from './pages/admin/BroadcastNotifications';
import DrinksCatalogManagement from './pages/admin/DrinksCatalogManagement';

// Sales Panel
import SalesLayout from './pages/sales/SalesLayout';
import SalesDashboard from './pages/sales/SalesDashboard';
import SalesRestaurants from './pages/sales/SalesRestaurants';
import SalesAddRestaurant from './pages/sales/SalesAddRestaurant';
import SalesEarnings from './pages/sales/SalesEarnings';
import SalesProfile from './pages/sales/SalesProfile';
import SalesSupport from './pages/sales/SalesSupport';

// Waiter Panel
import WaiterLayout from './pages/waiter/WaiterLayout';
import WaiterDashboard from './pages/waiter/WaiterDashboard';
import WaiterLogin from './pages/waiter/WaiterLogin';
import WaiterRequests from './pages/waiter/WaiterRequests';

// Staff Login (Universal)
import StaffLogin from './pages/staff/StaffLogin';

// Driver Application
import DriverApplicationPage from './pages/DriverApplicationPage';
import DriverDocumentUploadPage from './pages/DriverDocumentUploadPage';
import DriverDocumentPolicyPage from './pages/DriverDocumentPolicyPage';
import SalespersonApplicationPage from './pages/SalespersonApplicationPage';
import ContractorTermsPage from './pages/ContractorTermsPage';
import OrderTrackingPage from './pages/OrderTrackingPage';

// Static Pages
import AboutPage from './pages/AboutPage';
import ContactPage from './pages/ContactPage';
import FAQPage from './pages/FAQPage';
import PrivacyPage from './pages/PrivacyPage';
import TermsPage from './pages/TermsPage';
import BuyCreditsPage from './pages/BuyCreditsPage';
import VeriFactuPage from './pages/VeriFactuPage';

// Driver Panel
import DriverLayout from './pages/driver/DriverLayout';
import DriverDashboard from './pages/driver/DriverDashboard';
import DriverEarnings from './pages/driver/DriverEarnings';
import DriverSettings from './pages/driver/DriverSettings';

// Check if we're on admin subdomain
const isAdminSubdomain = () => {
  const hostname = window.location.hostname;
  return hostname.startsWith('admin.');
};

// Protected Route Component
const ProtectedRoute = ({ children, roles = [] }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="spinner"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (roles.length > 0 && !roles.includes(user.role)) {
    return <Navigate to="/" replace />;
  }

  return children;
};

// Public Layout with Navbar and Footer
const PublicLayout = ({ children, showFooter = true }) => {
  return (
    <>
      <Navbar />
      {children}
      {showFooter && <Footer />}
    </>
  );
};

// Super Admin Login Page (special route)
const SuperAdminLoginPage = () => {
  return <LoginPage isSuperAdmin={true} />;
};

// Admin Subdomain Routes
function AdminSubdomainRoutes() {
  return (
    <Routes>
      {/* Super Admin Login - /sad/login */}
      <Route path="/sad/login" element={<SuperAdminLoginPage />} />
      
      {/* Restaurant/Regular Admin Login - /login */}
      <Route path="/login" element={<LoginPage />} />
      <Route path="/register" element={<RegisterPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/auth/callback" element={<AuthCallbackPage />} />
      
      {/* Super Admin Panel */}
      <Route path="/admin/*" element={
        <ProtectedRoute roles={['super_admin']}>
          <AdminLayout />
        </ProtectedRoute>
      }>
        <Route index element={<AdminDashboard />} />
        <Route path="server-health" element={<ServerHealthDashboard />} />
        <Route path="subscriptions" element={<SubscriptionManagement />} />
        <Route path="restaurants" element={<RestaurantManagement />} />
        <Route path="users" element={<UserManagement />} />
        <Route path="applications" element={<ApplicationsManagement />} />
        <Route path="driver-documents" element={<DriverDocumentVerification />} />
        <Route path="document-archives" element={<DocumentArchives />} />
        <Route path="drinks-catalog" element={<DrinksCatalogManagement />} />
        <Route path="reports" element={<FinancialReports />} />
        <Route path="analytics" element={<AnalyticsDashboard />} />
        <Route path="promo-codes" element={<PromoCodes />} />
        <Route path="delivery-settings" element={<DeliverySettings />} />
        <Route path="driver-performance" element={<DriverPerformance />} />
        <Route path="salesperson-payouts" element={<SalespersonPayouts />} />
        <Route path="audit" element={<AuditLogs />} />
        <Route path="setup-wizard" element={<SetupWizard />} />
        <Route path="tickets" element={<TicketManagement />} />
        <Route path="settings" element={<PlatformSettings />} />
        <Route path="notifications" element={<NotificationManagement />} />
        <Route path="broadcast" element={<BroadcastNotifications />} />
      </Route>
      
      {/* Restaurant Panel */}
      <Route path="/restaurant/*" element={
        <ProtectedRoute roles={['restaurant_admin', 'super_admin', 'restaurant_manager', 'restaurant_cashier', 'restaurant_kitchen']}>
          <RestaurantLayout />
        </ProtectedRoute>
      }>
        <Route index element={<RestaurantDashboard />} />
        <Route path="menu" element={<MenuManagement />} />
        <Route path="stock" element={<StockManagement />} />
        <Route path="orders" element={<RestaurantOrders />} />
        <Route path="pos" element={<POS />} />
        <Route path="kitchen" element={<Kitchen />} />
        <Route path="drivers" element={<DriverMap />} />
        <Route path="table-management" element={<TableManagement />} />
        <Route path="tables" element={<TableBookings />} />
        <Route path="live-tables" element={<LiveTables />} />
        <Route path="waiters" element={<WaiterManagement />} />
        <Route path="staff" element={<StaffManagement />} />
        <Route path="loyalty" element={<LoyaltyProgram />} />
        <Route path="reports" element={<RestaurantFinancialReports />} />
        <Route path="eod-reports" element={<EODReports />} />
        <Route path="print-settings" element={<PrintSettingsPage />} />
        <Route path="verifactu" element={<VeriFactuDashboard />} />
        <Route path="verifactu/settings" element={<VeriFactuSettings />} />
        <Route path="settings" element={<RestaurantSettings />} />
        <Route path="support" element={<SupportTickets />} />
      </Route>
      
      {/* Driver Panel */}
      <Route path="/driver/*" element={
        <ProtectedRoute roles={['driver', 'super_admin']}>
          <DriverLayout />
        </ProtectedRoute>
      }>
        <Route index element={<DriverDashboard />} />
        <Route path="deliveries" element={<div className="text-center py-16 text-slate-500">All deliveries coming soon</div>} />
        <Route path="earnings" element={<DriverEarnings />} />
        <Route path="settings" element={<DriverSettings />} />
      </Route>
      
      {/* Sales Portal */}
      <Route path="/sales/*" element={
        <ProtectedRoute roles={['sales_person', 'super_admin']}>
          <SalesLayout />
        </ProtectedRoute>
      }>
        <Route index element={<SalesDashboard />} />
        <Route path="restaurants" element={<SalesRestaurants />} />
        <Route path="restaurants/add" element={<SalesAddRestaurant />} />
        <Route path="earnings" element={<SalesEarnings />} />
        <Route path="support" element={<SalesSupport />} />
        <Route path="profile" element={<SalesProfile />} />
      </Route>
      
      {/* Staff Login (Universal - all staff roles) */}
      <Route path="/staff-login" element={<StaffLogin />} />
      <Route path="/staff/login" element={<Navigate to="/staff-login" replace />} />
      
      {/* Waiter Portal */}
      <Route path="/waiter-login" element={<WaiterLogin />} />
      <Route path="/waiter/login" element={<Navigate to="/waiter-login" replace />} />
      <Route path="/waiter/*" element={
        <ProtectedRoute roles={['waiter', 'super_admin']}>
          <WaiterLayout />
        </ProtectedRoute>
      }>
        <Route index element={<WaiterDashboard />} />
        <Route path="requests" element={<WaiterRequests />} />
        <Route path="profile" element={<div className="text-center py-16 text-slate-500">Profile page coming soon</div>} />
      </Route>
      
      {/* Default redirect based on role */}
      <Route path="/" element={<AdminHomeRedirect />} />
      <Route path="*" element={<Navigate to="/login" replace />} />
    </Routes>
  );
}

// Redirect admin subdomain home based on user role
function AdminHomeRedirect() {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="spinner"></div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  switch (user.role) {
    case 'super_admin':
      return <Navigate to="/admin" replace />;
    case 'restaurant_admin':
      return <Navigate to="/restaurant" replace />;
    case 'driver':
      return <Navigate to="/driver" replace />;
    case 'sales_person':
      return <Navigate to="/sales" replace />;
    case 'waiter':
      return <Navigate to="/waiter" replace />;
    default:
      return <Navigate to="/login" replace />;
  }
}

// Main App Routes (for hambre.app)
function MainAppRoutes() {
  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={<PublicLayout><RestaurantOSLandingPage /></PublicLayout>} />
      <Route path="/delivery" element={<PublicLayout><LandingPage /></PublicLayout>} />
      <Route path="/login" element={<PublicLayout showFooter={false}><LoginPage /></PublicLayout>} />
      <Route path="/register" element={<PublicLayout showFooter={false}><RegisterPage /></PublicLayout>} />
      <Route path="/forgot-password" element={<PublicLayout showFooter={false}><ForgotPasswordPage /></PublicLayout>} />
      <Route path="/auth/callback" element={<AuthCallbackPage />} />
      <Route path="/restaurants" element={<PublicLayout><RestaurantsPage /></PublicLayout>} />
      <Route path="/restaurant/:id" element={<PublicLayout showFooter={false}><RestaurantDetailPage /></PublicLayout>} />
      <Route path="/book-table/:id" element={<PublicLayout showFooter={false}><BookTablePage /></PublicLayout>} />
      <Route path="/cart" element={<PublicLayout showFooter={false}><CartPage /></PublicLayout>} />
      <Route path="/order-success" element={<PublicLayout showFooter={false}><OrderSuccessPage /></PublicLayout>} />
      
      {/* Application Pages */}
      <Route path="/apply/driver" element={<DriverApplicationPage />} />
      <Route path="/apply/driver/documents" element={<DriverDocumentUploadPage />} />
      <Route path="/apply/salesperson" element={<SalespersonApplicationPage />} />
      <Route path="/contractor-terms" element={<ContractorTermsPage />} />
      <Route path="/driver-document-policy" element={<DriverDocumentPolicyPage />} />
      
      {/* In-House Table Ordering - Public access (menu browsing), auth needed for ordering */}
      <Route path="/table-order/:id" element={
        <PublicLayout showFooter={false}><TableOrderingPage /></PublicLayout>
      } />
      
      {/* Staff Login (Universal - all staff roles) */}
      <Route path="/staff-login" element={<StaffLogin />} />
      <Route path="/staff/login" element={<Navigate to="/staff-login" replace />} />
      
      {/* Waiter Portal */}
      <Route path="/waiter-login" element={<WaiterLogin />} />
      <Route path="/waiter/login" element={<Navigate to="/waiter-login" replace />} />
      <Route path="/waiter/*" element={
        <ProtectedRoute roles={['waiter', 'super_admin']}>
          <WaiterLayout />
        </ProtectedRoute>
      }>
        <Route index element={<WaiterDashboard />} />
        <Route path="requests" element={<WaiterRequests />} />
        <Route path="profile" element={<div className="text-center py-16 text-slate-500">Profile page coming soon</div>} />
      </Route>
      
      {/* Static Pages */}
      <Route path="/about" element={<PublicLayout><AboutPage /></PublicLayout>} />
      <Route path="/contact" element={<PublicLayout><ContactPage /></PublicLayout>} />
      <Route path="/faq" element={<PublicLayout><FAQPage /></PublicLayout>} />
      <Route path="/privacy" element={<PublicLayout><PrivacyPage /></PublicLayout>} />
      <Route path="/terms" element={<PublicLayout><TermsPage /></PublicLayout>} />
      <Route path="/verifactu" element={<PublicLayout><VeriFactuPage /></PublicLayout>} />
      <Route path="/buy-credits" element={<PublicLayout><BuyCreditsPage /></PublicLayout>} />
      
      {/* Protected Customer Routes */}
      <Route path="/orders" element={
        <ProtectedRoute>
          <PublicLayout showFooter={false}><OrdersPage /></PublicLayout>
        </ProtectedRoute>
      } />
      <Route path="/order/:id" element={
        <ProtectedRoute>
          <PublicLayout showFooter={false}><OrderDetailPage /></PublicLayout>
        </ProtectedRoute>
      } />
      <Route path="/order/:orderId/track" element={
        <ProtectedRoute>
          <OrderTrackingPage />
        </ProtectedRoute>
      } />
      <Route path="/notifications" element={
        <ProtectedRoute>
          <PublicLayout showFooter={false}><NotificationsPage /></PublicLayout>
        </ProtectedRoute>
      } />
      <Route path="/notification-settings" element={
        <ProtectedRoute>
          <NotificationSettingsPage />
        </ProtectedRoute>
      } />
      <Route path="/profile" element={
        <ProtectedRoute>
          <ProfilePage />
        </ProtectedRoute>
      } />

      {/* Super Admin Route (also accessible from main domain) */}
      <Route path="/sad/login" element={<PublicLayout showFooter={false}><SuperAdminLoginPage /></PublicLayout>} />
      
      {/* Restaurant Admin Routes (also accessible from main domain) */}
      <Route path="/restaurant/*" element={
        <ProtectedRoute roles={['restaurant_admin', 'super_admin', 'restaurant_manager', 'restaurant_cashier', 'restaurant_kitchen']}>
          <RestaurantLayout />
        </ProtectedRoute>
      }>
        <Route index element={<RestaurantDashboard />} />
        <Route path="menu" element={<MenuManagement />} />
        <Route path="stock" element={<StockManagement />} />
        <Route path="orders" element={<RestaurantOrders />} />
        <Route path="pos" element={<POS />} />
        <Route path="kitchen" element={<Kitchen />} />
        <Route path="drivers" element={<DriverMap />} />
        <Route path="table-management" element={<TableManagement />} />
        <Route path="tables" element={<TableBookings />} />
        <Route path="live-tables" element={<LiveTables />} />
        <Route path="waiters" element={<WaiterManagement />} />
        <Route path="staff" element={<StaffManagement />} />
        <Route path="loyalty" element={<LoyaltyProgram />} />
        <Route path="reports" element={<RestaurantFinancialReports />} />
        <Route path="eod-reports" element={<EODReports />} />
        <Route path="print-settings" element={<PrintSettingsPage />} />
        <Route path="verifactu" element={<VeriFactuDashboard />} />
        <Route path="verifactu/settings" element={<VeriFactuSettings />} />
        <Route path="settings" element={<RestaurantSettings />} />
        <Route path="support" element={<SupportTickets />} />
      </Route>

      {/* Super Admin Routes */}
      <Route path="/admin/*" element={
        <ProtectedRoute roles={['super_admin']}>
          <AdminLayout />
        </ProtectedRoute>
      }>
        <Route index element={<AdminDashboard />} />
        <Route path="server-health" element={<ServerHealthDashboard />} />
        <Route path="subscriptions" element={<SubscriptionManagement />} />
        <Route path="restaurants" element={<RestaurantManagement />} />
        <Route path="users" element={<UserManagement />} />
        <Route path="applications" element={<ApplicationsManagement />} />
        <Route path="driver-documents" element={<DriverDocumentVerification />} />
        <Route path="document-archives" element={<DocumentArchives />} />
        <Route path="drinks-catalog" element={<DrinksCatalogManagement />} />
        <Route path="reports" element={<FinancialReports />} />
        <Route path="analytics" element={<AnalyticsDashboard />} />
        <Route path="promo-codes" element={<PromoCodes />} />
        <Route path="delivery-settings" element={<DeliverySettings />} />
        <Route path="driver-performance" element={<DriverPerformance />} />
        <Route path="salesperson-payouts" element={<SalespersonPayouts />} />
        <Route path="audit" element={<AuditLogs />} />
        <Route path="setup-wizard" element={<SetupWizard />} />
        <Route path="tickets" element={<TicketManagement />} />
        <Route path="settings" element={<PlatformSettings />} />
        <Route path="notifications" element={<NotificationManagement />} />
        <Route path="broadcast" element={<BroadcastNotifications />} />
      </Route>

      {/* Driver Routes */}
      <Route path="/driver/*" element={
        <ProtectedRoute roles={['driver', 'super_admin']}>
          <DriverLayout />
        </ProtectedRoute>
      }>
        <Route index element={<DriverDashboard />} />
        <Route path="deliveries" element={<div className="text-center py-16 text-slate-500">All deliveries coming soon</div>} />
        <Route path="earnings" element={<DriverEarnings />} />
        <Route path="settings" element={<DriverSettings />} />
      </Route>

      {/* Sales Routes */}
      <Route path="/sales/*" element={
        <ProtectedRoute roles={['sales_person', 'super_admin']}>
          <SalesLayout />
        </ProtectedRoute>
      }>
        <Route index element={<SalesDashboard />} />
        <Route path="restaurants" element={<SalesRestaurants />} />
        <Route path="restaurants/add" element={<SalesAddRestaurant />} />
        <Route path="earnings" element={<SalesEarnings />} />
        <Route path="support" element={<SalesSupport />} />
        <Route path="profile" element={<SalesProfile />} />
      </Route>

      {/* Fallback */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function AppRoutes() {
  // Check if on admin subdomain
  if (isAdminSubdomain()) {
    return <AdminSubdomainRoutes />;
  }
  return <MainAppRoutes />;
}

function App() {
  return (
    <HelmetProvider>
      <BrowserRouter>
        <AuthProvider>
          <OfflineProvider>
            <CurrencyProvider>
              <CartProvider>
                <div className="App">
                  <OfflineWrapper>
                    <AppRoutes />
                  </OfflineWrapper>
                  <Toaster 
                    position="top-right" 
                    richColors 
                    closeButton
                    toastOptions={{
                      style: {
                        background: 'white',
                        border: '1px solid #e2e8f0',
                      },
                    }}
                  />
                </div>
              </CartProvider>
            </CurrencyProvider>
          </OfflineProvider>
        </AuthProvider>
      </BrowserRouter>
    </HelmetProvider>
  );
}

export default App;
